import React, { ReactNode, useCallback, useEffect, useMemo } from "react";

import AsyncStorage from "@react-native-async-storage/async-storage";
import { useUser } from "../Login/State";

type CtxType = {
  visitedObjects: { [key: string]: string };
  notifyVisitedObject: (type: ObjectType, teamId: string, id?: string) => void;
};
const Ctx = React.createContext<CtxType>({
  visitedObjects: {},
  notifyVisitedObject: () => {},
});

export const useLastVisitedObjectAtTeam = (
  type: ObjectType,
  teamId: string,
  id?: string
): Date | null => {
  const v = React.useContext(Ctx);
  return useMemo(() => {
    const key = `${teamId}${type}${id ?? ""}`;
    return v.visitedObjects[key] ? new Date(v.visitedObjects[key]) : null;
  }, [teamId, type, id, v.visitedObjects]);
};

type ObjectType = "todoCategory" | "team" | "routine" | "issue";

const useNotifyVisitedObject = () => React.useContext(Ctx).notifyVisitedObject;

export const useNotifyVisitedObjectAtLeaveNav = (
  type: ObjectType,
  teamId: string,
  id?: string
) => {
  const notify = useNotifyVisitedObject();
  useEffect(() => {
    return () => notify(type, teamId, id);
  }, [id, notify, teamId, type]);
};

const d = {
  notifyVisitedObject() {},
  visitedObjects: {},
};
export const LastVisitedTeamProvider = ({
  children,
  storeId,
}: {
  children?: ReactNode | undefined;
  storeId: string | null;
}) => {
  if (storeId) {
    return (
      <LastVisitedTeamProviderC storeId={storeId}>
        {children}
      </LastVisitedTeamProviderC>
    );
  }

  return <Ctx.Provider value={d}>{children}</Ctx.Provider>;
};

const LastVisitedTeamProviderC = ({
  children,
  storeId,
}: {
  children?: ReactNode | undefined;
  storeId: string;
}) => {
  const user = useUser();

  const storeKey = useMemo(
    () => "v4.lastVisitedTeam" + user.user.sub + storeId,
    [storeId, user.user.sub]
  );

  const [lastVisitedMap, setLastVisitedMap] = React.useState<{
    [key: string]: string;
  }>({});
  useEffect(() => {
    AsyncStorage.getItem(storeKey).then((value) => {
      if (value) {
        setLastVisitedMap(JSON.parse(value));
      }
    });
  }, [storeKey]);

  useEffect(() => {
    if (Object.keys(lastVisitedMap).length > 0) {
      AsyncStorage.setItem(storeKey, JSON.stringify(lastVisitedMap));
    }
  }, [lastVisitedMap, storeKey]);

  const notifyVisitedObject = useCallback(
    (type: ObjectType, teamId: string, id?: string) => {
      const key = `${teamId}${type}${id ?? ""}`;

      setLastVisitedMap((prev) => ({
        ...prev,

        [key]: new Date().toISOString(),
      }));
    },
    [setLastVisitedMap]
  );

  const val = useMemo(
    () => ({
      visitedObjects: lastVisitedMap,
      notifyVisitedObject,
    }),
    [lastVisitedMap, notifyVisitedObject]
  );

  return <Ctx.Provider value={val}>{children}</Ctx.Provider>;
};
